import React, { useCallback, useEffect, useState } from 'react'

import { Box, Button, CircularProgress, Typography } from '@material-ui/core'
import { ChevronLeft, ChevronRight, Download, Folder } from 'react-feather'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'

import useAuthTraining from 'hooks/useAuthTraining'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import { routes } from 'TrainingRoutes'
import useStyles from './styles'

const DetailRow = ({ label, value, classes }) => (
  <Box className={classes.detailRow}>
    <Typography className={classes.detailLabel}>{label}</Typography>
    <Typography className={classes.detailValue}>{value}</Typography>
  </Box>
)

const CourseCertificate = ({ match, history }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { portalToken: contextPortalToken } = useAuthTraining()

  const portalToken = match?.params?.portalToken || contextPortalToken
  const courseId = match?.params?.courseId

  const [certificateData, setCertificateData] = useState(null)
  const [prevLessonId, setPrevLessonId] = useState(null)
  const [prevLessonKind, setPrevLessonKind] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [certificateResponse, modulesResponse] = await Promise.all([
        service.trainingPortal.courses.certificate(courseId),
        service.trainingPortal.courses.modules(courseId),
      ])

      setCertificateData(certificateResponse?.data?.certificate || null)

      const modules = modulesResponse?.data?.modules || []
      const allLessons = modules.flatMap(
        (courseModule) => courseModule.lessons || [],
      )
      if (!isEmpty(allLessons)) {
        const lastLesson = allLessons[allLessons.length - 1]
        setPrevLessonId(lastLesson?.id || null)
        setPrevLessonKind(lastLesson?.kind || null)
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao carregar o certificado.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [courseId, snackbar])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line
  }, [courseId])

  const handlePrevLesson = () => {
    if (!prevLessonId) return
    const targetRoute =
      prevLessonKind === 'exercise'
        ? routes.exerciseContent
        : routes.lessonContent
    history.push(
      targetRoute
        .replace(':portalToken', portalToken)
        .replace(':courseId', courseId)
        .replace(':lessonId', prevLessonId),
    )
  }

  const handleGoHome = () => {
    history.push(routes.home.replace(':portalToken', portalToken))
  }

  const handleDownload = async () => {
    const certificateUrl = certificateData?.certificateUrl
    if (!certificateUrl) return

    setIsDownloading(true)
    try {
      const token = service.trainingPortal.auth.getToken()
      const response = await fetch(certificateUrl, {
        headers: { Authorization: token },
      })

      if (!response.ok) {
        snackbar.open({
          message: 'Não foi possível gerar o certificado. Tente novamente.',
          variant: 'error',
        })
        return
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `certificado-${courseId}.pdf`
      link.click()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      snackbar.open({
        message: 'Erro ao baixar o certificado. Verifique sua conexão.',
        variant: 'error',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const passed = certificateData?.passed
  const allLessonsCompleted = certificateData?.allLessonsCompleted
  const studentName = certificateData?.studentName || ''
  const courseTitle = certificateData?.course?.title || ''
  const averageScore = certificateData?.averageScore ?? '-'
  const minPercentage = certificateData?.minPercentage ?? 0
  const completedAt = certificateData?.completedAt
    ? helpers.formatters.date(certificateData.completedAt)
    : '-'
  const modulesCount = certificateData?.course?.modulesCount ?? 0
  const lessonsCount = certificateData?.course?.lessonsCount ?? 0
  const totalDuration = certificateData?.course?.totalDuration || '-'
  const categories = certificateData?.course?.categories || []
  const categoryName = isEmpty(categories)
    ? '-'
    : categories.map((category) => category.name).join(', ')

  return (
    <Box className={classes.root}>
      <LoadingFeedback open={isLoading} />

      {!isLoading && certificateData && (
        <>
          <Box className={classes.hero}>
            <Box className={classes.heroInner}>
              <Box className={classes.heroLeft}>
                {passed ? (
                  <Typography className={classes.congratsTitle}>
                    Parabéns {studentName}!
                  </Typography>
                ) : (
                  <Typography className={classes.failTitle}>
                    Curso não concluído
                  </Typography>
                )}

                {passed ? (
                  <Typography className={classes.heroSubtitle}>
                    Você concluiu o curso <strong>{courseTitle}</strong>.
                    <br />
                    Seu certificado de conclusão já foi gerado e está pronto
                    para download. Adicione essa competência ao seu currículo
                    agora mesmo.
                  </Typography>
                ) : !allLessonsCompleted ? (
                  <Typography className={classes.heroSubtitle}>
                    Você ainda não concluiu todas as aulas do curso{' '}
                    <strong>{courseTitle}</strong>.
                    <br />
                    Finalize as aulas e o exercício pendentes para gerar o
                    certificado.
                  </Typography>
                ) : (
                  <Typography className={classes.heroSubtitle}>
                    Você não atingiu a nota mínima necessária para concluir o
                    curso <strong>{courseTitle}</strong>.
                    <br />
                    Sua média foi de <strong>{averageScore}</strong> e a nota
                    mínima exigida é de <strong>{minPercentage}%</strong>.
                    Revise as aulas e tente novamente para obter o certificado.
                  </Typography>
                )}

                {isDownloading ? (
                  <Box className={classes.downloadFeedback}>
                    <CircularProgress size={18} color="secondary" />
                    <Typography
                      variant="body2"
                      className={classes.downloadFeedbackText}
                    >
                      Baixando certificado...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {passed && (
                      <Button
                        variant="contained"
                        className={classes.downloadButton}
                        startIcon={<Download size={18} />}
                        onClick={handleDownload}
                      >
                        Baixar Certificado
                      </Button>
                    )}
                  </>
                )}
              </Box>

              {passed && certificateData?.svgUrl && (
                <Box
                  className={classes.heroRight}
                  style={{ backgroundColor: certificateData?.secondaryColor }}
                >
                  <img
                    src={certificateData.svgUrl}
                    alt="Certificado de conclusão"
                    className={classes.certificatePreview}
                  />
                  <Typography
                    className={classes.certificateLabel}
                    style={{ color: certificateData?.primaryColor }}
                  >
                    Certificado de Conclusão
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box className={classes.body}>
            <Box className={classes.detailsSection}>
              <DetailRow
                label="Nome do curso"
                value={courseTitle}
                classes={classes}
              />
              <DetailRow
                label="Categoria"
                value={categoryName}
                classes={classes}
              />
              <DetailRow
                label="Carga horária"
                value={totalDuration}
                classes={classes}
              />
              <DetailRow
                label="Módulos"
                value={modulesCount}
                classes={classes}
              />
              <DetailRow
                label="Aulas"
                value={`${lessonsCount} Aulas`}
                classes={classes}
              />
              <DetailRow
                label="Data de conclusão"
                value={completedAt}
                classes={classes}
              />
              <DetailRow
                label="Média obtida"
                value={`${averageScore}%`}
                classes={classes}
              />
              <DetailRow
                label="Nota mínima"
                value={`${minPercentage}%`}
                classes={classes}
              />
            </Box>

            {passed && (
              <Box className={classes.storageBox}>
                <Box className={classes.storageIconBox}>
                  <Folder size={28} className={classes.storageIcon} />
                </Box>
                <Typography className={classes.storageTitle}>
                  Não quer baixar agora? Sem problemas!
                </Typography>
                <Typography className={classes.storageSubtitle}>
                  Seu certificado ficará guardado na aba &apos;Perfil&apos;,
                  dentro da seção &apos;Certificados&apos;.
                </Typography>
              </Box>
            )}
          </Box>

          <Box className={classes.navigation}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ChevronLeft size={16} />}
              onClick={handlePrevLesson}
              disabled={!prevLessonId}
              className={classes.prevButton}
            >
              ANTERIOR
            </Button>
            <Button
              variant="contained"
              size="small"
              endIcon={<ChevronRight size={16} />}
              onClick={handleGoHome}
              className={classes.homeButton}
            >
              VOLTAR AO INÍCIO
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}

export default CourseCertificate
